import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { refreshAdminNotifications } from "@/components/NotificationBell";

const REASONS = ["잘못 생성", "중복 생성", "테스트 데이터", "고객 요청", "계약 취소", "기타"];
const TYPE_LABEL = { quote: "견적", contract: "계약", consultation: "상담기록" };

export default function DeleteRequestModal({ resourceType, resourceId, resourceTitle, customerName, onClose, onDone }) {
  const { user } = useAuth();
  const isOwner = user?.role === "super_admin";
  const [reason, setReason] = useState("잘못 생성");
  const [detail, setDetail] = useState("");
  const [busy, setBusy] = useState(false);
  const kind = TYPE_LABEL[resourceType] || resourceType;

  const submitOwner = async () => {
    setBusy(true);
    try {
      const path = resourceType === "quote"
        ? `/admin/quotes/${resourceId}`
        : resourceType === "contract"
          ? `/admin/contracts/${resourceId}`
          : `/admin/consultations/${resourceId}`;
      await api.delete(path, { params: detail ? { note: detail } : {} });
      toast.success("보관 처리되었습니다.");
      refreshAdminNotifications();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "처리 실패");
    } finally {
      setBusy(false);
    }
  };

  const submitSales = async () => {
    if (reason === "기타" && !detail.trim()) {
      return toast.error("기타 사유를 입력해 주세요.");
    }
    setBusy(true);
    try {
      await api.post("/admin/delete-requests", {
        resource_type: resourceType,
        resource_id: resourceId,
        reason,
        reason_detail: detail,
      });
      toast.success("삭제 요청을 보냈습니다. OWNER 승인 전까지 원본은 유지됩니다.");
      refreshAdminNotifications();
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "처리 실패");
    } finally {
      setBusy(false);
    }
  };

  if (isOwner) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="owner-archive-modal">
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 w-full max-w-md space-y-3">
          <h4 className="font-medium">이 {kind}을 보관하시겠습니까?</h4>
          <p className="text-sm text-white/70">데이터 종류: {kind}</p>
          <p className="text-sm text-white/70">대상명: {resourceTitle || "-"}</p>
          <p className="text-sm text-white/70">고객명: {customerName || "-"}</p>
          {resourceType === "contract" && (
            <p className="text-xs text-amber-300">계약은 법적/업무 기록이므로 보관(Archive) 처리됩니다.</p>
          )}
          <textarea rows={2} placeholder="보관 사유 (선택)" value={detail} onChange={(e) => setDetail(e.target.value)} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
          <div className="flex gap-2">
            <button type="button" disabled={busy} onClick={submitOwner} className="bg-white text-black rounded-lg px-3 py-2 text-sm" data-testid="owner-archive-submit">보관 실행</button>
            <button type="button" onClick={onClose} className="border border-white/10 rounded-lg px-3 py-2 text-sm">취소</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="delete-request-modal">
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 w-full max-w-md space-y-3">
        <h4 className="font-medium">삭제 요청</h4>
        <p className="text-xs text-white/50">대상: {resourceTitle || "-"} · 고객: {customerName || "-"} · {kind}</p>
        <select value={reason} onChange={(e) => setReason(e.target.value)} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm">
          {REASONS.map((r) => <option key={r}>{r}</option>)}
        </select>
        <textarea rows={3} placeholder={reason === "기타" ? "상세 사유 (필수)" : "상세 사유 (선택)"} value={detail} onChange={(e) => setDetail(e.target.value)} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
        <div className="flex gap-2">
          <button type="button" disabled={busy} onClick={submitSales} className="bg-white text-black rounded-lg px-3 py-2 text-sm" data-testid="delete-request-submit">요청 보내기</button>
          <button type="button" onClick={onClose} className="border border-white/10 rounded-lg px-3 py-2 text-sm">취소</button>
        </div>
      </div>
    </div>
  );
}
